import React from 'react';
import { motion } from 'framer-motion';

/**
 * BriefingStrip — recruiter-friendly elevator pitch directly under the hero
 * stats. Three columns: CURRENT FOCUS · PROVEN IN · OPEN TO. Designed so a
 * recruiter who never scrolls past the first viewport still leaves with
 * the full positioning in under 4 seconds.
 */
const COLUMNS = [
  {
    label: 'Current Focus',
    items: ['Cloud Security Architecture', 'AI / ML Systems', 'Product Strategy'],
  },
  {
    label: 'Proven In',
    items: ['Enterprise · Government', 'Healthcare · Education', 'Hardware · Industrial IoT'],
  },
  {
    label: 'Open To',
    items: ['Senior IC · Staff Engineer', 'Architecture Leadership', 'Founding Engineer'],
  },
];

export default function BriefingStrip() {
  return (
    <section
      className="relative border-b border-white/8 bg-black/35 backdrop-blur-[2px]"
      data-testid="briefing-strip"
      aria-label="At-a-glance professional briefing"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-7 sm:py-9">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-2.5 mb-5 sm:mb-6"
        >
          <span
            className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ background: 'var(--hyper-cyan)', boxShadow: '0 0 6px var(--hyper-cyan)' }}
          />
          <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.4em] uppercase text-[var(--hyper-cyan)]">
            Briefing / At-A-Glance
          </span>
          <span className="hidden sm:block flex-1 h-px bg-white/8 ml-2" />
          <span className="hidden sm:block font-mono text-[9px] tracking-[0.28em] uppercase text-white/30">
            T+04s read
          </span>
        </motion.div>

        {/* Three columns */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8">
          {COLUMNS.map((col, ci) => (
            <motion.div
              key={col.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: 0.08 * ci, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              {/* column index pseudo-tag */}
              <div className="flex items-center gap-2 mb-2.5">
                <span className="font-mono text-[9px] tracking-[0.32em] uppercase text-white/30">
                  / 0{ci + 1}
                </span>
                <span className="block h-px flex-1 bg-white/10" />
              </div>
              <h3 className="font-mono text-[11px] sm:text-[12px] tracking-[0.3em] uppercase text-white mb-3">
                {col.label}
              </h3>
              <ul className="space-y-1.5">
                {col.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 font-rajdhani text-[15px] sm:text-base text-white/65 leading-snug">
                    <span className="text-[var(--hyper-cyan)] text-xs mt-1 shrink-0">▸</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
